import { useQueryClient } from '@tanstack/react-query';
import { CheckIcon, Loader2Icon } from 'lucide-react';
import { type JSX, useEffect, useState } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import type { BlockingRow } from '@/features/microsoft/picker-chrome.js';
import {
  createSharePointPlace,
  createSharePointSetupSession,
  listSharePointSetupDrives,
  listSharePointSetupItems,
  listSharePointSetupSites,
  type SharePointDrive,
  type SharePointItem,
  type SharePointLocated,
  type SharePointPlace,
  type SharePointSite,
} from '@/lib/api/sharepoint.js';
import {
  createKnowledgeSource,
  KnowledgeSourceAlreadyAdded,
  knowledgeSourceKeys,
} from '@/lib/api/workspace-knowledge.js';
import { notify } from '@/lib/notify.js';

import {
  SharePointBreadcrumb,
  SharePointDriveList,
  SharePointItemList,
  SharePointSiteList,
  type BrowseCrumb,
} from './BrowseLevelList.js';
import type { BrowseList } from './browse-levels.js';
import type { BrowsePosition, RowStanding } from './place-coverage.js';
import { PlaceFinder } from './PlaceFinder.js';

export type { BrowsePosition } from './place-coverage.js';

/**
 * Only one browse or add action is ever in flight at a time — the browser shows one level at a
 * time, so there is no scenario where two levels load concurrently. `kind` decides which control
 * shows a spinner; every kind but `load-more` and `add` counts as loading the level itself.
 */
type PendingAction =
  | { kind: 'session' }
  | { kind: 'open-site' }
  | { kind: 'open-drive' }
  | { kind: 'open-folder' }
  | { kind: 'load-more' }
  | { kind: 'add'; rowId: string };

function errorText(reason: unknown, fallback: string): string {
  return reason instanceof Error && reason.message ? reason.message : fallback;
}

/**
 * Rows in the order a person reads them, not the order Graph returns them.
 *
 * Graph orders sites and libraries by its own relevance, which looks like no order at all to
 * somebody scanning seventy names for one. Sorting happens on what is loaded rather than on the
 * server, because these endpoints page and do not take an ordering — so a later page can still
 * bring a name that belongs higher, and it lands in place the moment it arrives.
 */
function byName<T extends { name: string }>(rows: readonly T[]): T[] {
  return [...rows].sort((left, right) =>
    left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }),
  );
}

/** Folders first, then files, each in reading order — a folder is a way deeper, a file is a leaf. */
function byFolderThenName(rows: readonly SharePointItem[]): SharePointItem[] {
  return [...rows].sort((left, right) => {
    if (left.isFolder !== right.isFolder) return left.isFolder ? -1 : 1;
    return left.name.localeCompare(right.name, undefined, {
      sensitivity: 'base',
    });
  });
}

/**
 * The rows with the one a search or a link led to moved to the top, so it is the first thing on
 * screen in a level that can hold hundreds.
 */
function foundFirst<T>(
  rows: readonly T[],
  foundId: string | null,
  idOf: (row: T) => string,
): T[] {
  const found = rows.find((row) => idOf(row) === foundId);
  return found
    ? [found, ...rows.filter((row) => idOf(row) !== foundId)]
    : [...rows];
}

/** The rows of a page, with the found one added when that page did not carry it. */
function withFound<T>(
  rows: readonly T[],
  found: T,
  idOf: (row: T) => string,
): T[] {
  return rows.some((row) => idOf(row) === idOf(found))
    ? [...rows]
    : [found, ...rows];
}

/** Turns one manually-fetched page into the shape {@link SharePointSiteList} and its siblings expect. */
function browseList<T>(
  rows: T[],
  cursor: string | null,
  loading: boolean,
  loadingMore: boolean,
  onLoadMore: () => void,
): BrowseList<T> {
  return {
    rows,
    isLoading: loading,
    isError: false,
    error: undefined,
    hasMore: cursor !== null,
    isLoadingMore: loadingMore,
    loadMore: onLoadMore,
  };
}

/** Said above an item found by search or link whose folders above are closed to this account. */
export const HIDDEN_PATH_NOTE =
  'The folders above this are not shared with your account, so it is shown on its own.';

/**
 * Said when a whole site or library lands in knowledge. It shares the one content limit a folder
 * has, so a big one is indexed only in part, and its folders are the way to all of it.
 */
export const WHOLE_SITE_OR_LIBRARY_NOTE = (name: string): string =>
  `“${name}” is in the knowledge base. A whole site or library can hold more than one knowledge source indexes — then the newest files are kept and the rest left out. To index all of it, add its folders instead.`;

/**
 * Everything a connection can reach, with an Add beside each row.
 *
 * The list is the point. Choosing what a connection exposes means comparing what is already on the
 * list against what sits beside it, and a picker behind a button showed only one of the two — you
 * had to remember the other. So this browses in place, under whatever section holds the chosen ones.
 *
 * A row is added AS IT IS: a whole site, a document library, a folder or a single file, each at
 * maximum access and indexed where the address allows it.
 *
 * Browsing runs under a short-lived setup capability rather than the connection's normal (possibly
 * already-restricted) browse: choosing a NEW place requires seeing the whole connection, which an
 * enforced restriction would otherwise refuse. The capability is asked for once, on mount, and
 * travels as a header on every browse call — never a query parameter, which is how the equivalent
 * OneDrive flow already does it, and how this feature's own server side had to fix a real leak once.
 * If the capability cannot be obtained, this says so and never falls back to browsing without it.
 *
 * Adding is a different call, on the caller's own platform JWT, not the setup capability — only a
 * connection's owner reaches this at all, so nothing about creating the row needs the reduced scope.
 */
export function SharePointPlaceBrowser({
  workspaceId,
  connectionId,
  onAdded,
  standingOf,
  coverOf,
  onReplace,
}: {
  workspaceId: string;
  connectionId: string;
  /** Called after each successful add, so the section above can re-read its list. */
  onAdded: (place?: SharePointPlace) => void;
  /**
   * How a row stands against the list already. Answered by the caller, which holds both the places
   * and the knowledge sources; the browser knows only where it is standing.
   */
  standingOf: (
    row: { kind: 'site' | 'library' | 'folder' | 'file'; id: string },
    position: BrowsePosition,
  ) => RowStanding;
  /**
   * Takes the named places off the list, so a wider one can be added over them. Left out by a
   * surface that cannot remove a place; its rows then stay refused rather than offering something
   * nobody can carry out.
   */
  onReplace?: (placeIds: readonly string[]) => Promise<void>;
  /** The place this level sits inside, if any — said once instead of on every row. */
  coverOf: (position: BrowsePosition) => string | null;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<{
    token: string;
    expiresAt: string;
  } | null>(null);

  const [siteId, setSiteId] = useState<string | null>(null);
  const [siteName, setSiteName] = useState<string | null>(null);
  const [driveId, setDriveId] = useState<string | null>(null);
  const [driveName, setDriveName] = useState<string | null>(null);
  const [folderStack, setFolderStack] = useState<
    { itemId: string; name: string }[]
  >([]);

  const [sites, setSites] = useState<SharePointSite[]>([]);
  const [sitesCursor, setSitesCursor] = useState<string | null>(null);
  const [drives, setDrives] = useState<SharePointDrive[]>([]);
  const [drivesCursor, setDrivesCursor] = useState<string | null>(null);
  const [items, setItems] = useState<SharePointItem[]>([]);
  const [itemsCursor, setItemsCursor] = useState<string | null>(null);

  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null,
  );
  /** The row a search or a pasted link led to, kept at the top of its level. */
  const [foundId, setFoundId] = useState<string | null>(null);
  /**
   * A found row shown on its own because the folders above it are closed to this account. It stays
   * "alone" only while it is the whole level: opening it, or going anywhere else, replaces the rows.
   */
  const [aloneId, setAloneId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The row waiting on a confirmation, with what it would take off the list and how to add it. */
  const [confirming, setConfirming] = useState<{
    rowId: string;
    name: string;
    blocking: readonly BlockingRow[];
    run: () => void;
  } | null>(null);
  const busy = pendingAction !== null;
  const loadingLevel =
    pendingAction !== null &&
    pendingAction.kind !== 'load-more' &&
    pendingAction.kind !== 'add';
  const loadingMore = pendingAction?.kind === 'load-more';
  const position: BrowsePosition = {
    siteId,
    driveId,
    folderItemIds: folderStack.map((folder) => folder.itemId),
  };

  useEffect(() => {
    if (!session && pendingAction === null && !error) void start();
  }, [session]);

  async function start(): Promise<void> {
    setPendingAction({ kind: 'session' });
    setError(null);
    try {
      const created = await createSharePointSetupSession(
        workspaceId,
        connectionId,
      );
      const page = await listSharePointSetupSites(
        workspaceId,
        connectionId,
        created.token,
      );
      setSession(created);
      setSites(page.items);
      setSitesCursor(page.nextCursor);
    } catch (reason) {
      setError(errorText(reason, 'Could not start browsing this SharePoint.'));
    } finally {
      setPendingAction(null);
    }
  }

  async function openSite(site: SharePointSite): Promise<void> {
    if (!session) return;
    setPendingAction({ kind: 'open-site' });
    setError(null);
    try {
      const page = await listSharePointSetupDrives(
        site.siteId,
        workspaceId,
        connectionId,
        session.token,
      );
      setSiteId(site.siteId);
      setSiteName(site.name);
      setDriveId(null);
      setDriveName(null);
      setFolderStack([]);
      setItems([]);
      setItemsCursor(null);
      setDrives(page.items);
      setDrivesCursor(page.nextCursor);
    } catch (reason) {
      setError(errorText(reason, 'Could not open this site.'));
    } finally {
      setPendingAction(null);
    }
  }

  async function openDrive(drive: SharePointDrive): Promise<void> {
    if (!session || !siteId) return;
    setPendingAction({ kind: 'open-drive' });
    setError(null);
    try {
      const page = await listSharePointSetupItems(
        drive.driveId,
        'root',
        workspaceId,
        connectionId,
        session.token,
      );
      setDriveId(drive.driveId);
      setDriveName(drive.name);
      setFolderStack([]);
      setItems(page.items);
      setItemsCursor(page.nextCursor);
    } catch (reason) {
      setError(errorText(reason, 'Could not open this document library.'));
    } finally {
      setPendingAction(null);
    }
  }

  async function openFolder(item: SharePointItem): Promise<void> {
    if (!session || !driveId) return;
    setPendingAction({ kind: 'open-folder' });
    setError(null);
    try {
      const page = await listSharePointSetupItems(
        driveId,
        item.itemId,
        workspaceId,
        connectionId,
        session.token,
      );
      setFolderStack((current) => [
        ...current,
        { itemId: item.itemId, name: item.name },
      ]);
      setItems(page.items);
      setItemsCursor(page.nextCursor);
    } catch (reason) {
      setError(errorText(reason, 'Could not open this folder.'));
    } finally {
      setPendingAction(null);
    }
  }

  /** Navigates so that `folderStack[targetIndex]` becomes the current folder; `-1` is the drive root. */
  async function backToFolder(targetIndex: number): Promise<void> {
    if (!session || !driveId) return;
    const parent = targetIndex >= 0 ? folderStack[targetIndex]!.itemId : 'root';
    setPendingAction({ kind: 'open-folder' });
    setError(null);
    try {
      const page = await listSharePointSetupItems(
        driveId,
        parent,
        workspaceId,
        connectionId,
        session.token,
      );
      setFolderStack(
        targetIndex >= 0 ? folderStack.slice(0, targetIndex + 1) : [],
      );
      setItems(page.items);
      setItemsCursor(page.nextCursor);
    } catch (reason) {
      setError(errorText(reason, 'Could not open this folder.'));
    } finally {
      setPendingAction(null);
    }
  }

  function backToSites(): void {
    setSiteId(null);
    setSiteName(null);
    setDriveId(null);
    setDriveName(null);
    setFolderStack([]);
    setDrives([]);
    setDrivesCursor(null);
    setItems([]);
    setItemsCursor(null);
  }

  function backToDrives(): void {
    setDriveId(null);
    setDriveName(null);
    setFolderStack([]);
    setItems([]);
    setItemsCursor(null);
  }

  /**
   * Opens the level that holds a place found by search or by link, with that place at the top.
   *
   * A found site may be one the site list never showed — search leaves some out, which is what the
   * link is for — so it is added to the list rather than looked for in it. A folder or file opens
   * its parent with every folder above it on the breadcrumbs, because the ancestors are what tell
   * this tree whether a place on the list already covers the row. The site's libraries are read
   * too, so its breadcrumb leads somewhere.
   *
   * An item shared on its own, in folders this account was never given, has no parent to open. It
   * is shown as the whole level instead, still with its Add and its standing — the library and site
   * it lives in are known, which is all a library or site place needs to cover it.
   */
  async function reveal(located: SharePointLocated): Promise<boolean> {
    if (!session) return false;
    const { site, drive, ancestors, target } = located;
    if (target.level === 'site') {
      setError(null);
      backToSites();
      setSites((current) => withFound(current, site, (row) => row.siteId));
      setFoundId(site.siteId);
      return true;
    }
    if (!drive) return false;
    setPendingAction({ kind: 'open-site' });
    setError(null);
    try {
      const parent = ancestors.at(-1)?.itemId ?? 'root';
      const [drivesPage, itemsPage] = await Promise.all([
        listSharePointSetupDrives(
          site.siteId,
          workspaceId,
          connectionId,
          session.token,
        ),
        target.level === 'library' || !located.pathComplete
          ? null
          : listSharePointSetupItems(
              drive.driveId,
              parent,
              workspaceId,
              connectionId,
              session.token,
            ),
      ]);
      setSiteId(site.siteId);
      setSiteName(site.name);
      setDrives(withFound(drivesPage.items, drive, (row) => row.driveId));
      setDrivesCursor(drivesPage.nextCursor);
      const foundItem: SharePointItem = {
        itemId: target.id,
        name: target.name,
        isFolder: target.level === 'folder',
        webUrl: target.webUrl,
      };
      if (!located.pathComplete) {
        setDriveId(drive.driveId);
        setDriveName(drive.name);
        setFolderStack(ancestors);
        setItems([foundItem]);
        setItemsCursor(null);
        setAloneId(target.id);
      } else if (itemsPage === null) {
        setDriveId(null);
        setDriveName(null);
        setFolderStack([]);
        setItems([]);
        setItemsCursor(null);
      } else {
        setDriveId(drive.driveId);
        setDriveName(drive.name);
        setFolderStack(ancestors);
        setItems(withFound(itemsPage.items, foundItem, (row) => row.itemId));
        setItemsCursor(itemsPage.nextCursor);
      }
      setFoundId(target.id);
      return true;
    } catch (reason) {
      setError(errorText(reason, 'Could not open this place.'));
      return false;
    } finally {
      setPendingAction(null);
    }
  }

  async function loadMore(): Promise<void> {
    if (!session) return;
    setPendingAction({ kind: 'load-more' });
    setError(null);
    try {
      if (driveId !== null) {
        if (!itemsCursor) return;
        const parent = folderStack.at(-1)?.itemId ?? 'root';
        const page = await listSharePointSetupItems(
          driveId,
          parent,
          workspaceId,
          connectionId,
          session.token,
          { cursor: itemsCursor },
        );
        setItems((current) => [...current, ...page.items]);
        setItemsCursor(page.nextCursor);
      } else if (siteId !== null) {
        if (!drivesCursor) return;
        const page = await listSharePointSetupDrives(
          siteId,
          workspaceId,
          connectionId,
          session.token,
          { cursor: drivesCursor },
        );
        setDrives((current) => [...current, ...page.items]);
        setDrivesCursor(page.nextCursor);
      } else {
        if (!sitesCursor) return;
        const page = await listSharePointSetupSites(
          workspaceId,
          connectionId,
          session.token,
          '',
          { cursor: sitesCursor },
        );
        setSites((current) => [...current, ...page.items]);
        setSitesCursor(page.nextCursor);
      }
    } catch (reason) {
      setError(errorText(reason, 'Could not load more.'));
    } finally {
      setPendingAction(null);
    }
  }

  /**
   * Registers what was just added with the workspace's knowledge, so it arrives searchable.
   *
   * Every level lands here, which is the whole point: whatever somebody may allow, they may index,
   * and an add grants both without a second decision. The address is what differs — a site anchors
   * on itself, a library on its drive, a folder and a file on their Graph item — and
   * {@link knowledgeAddressOf} is the one place that says so.
   *
   * A failure here does not undo what was added. Registering runs under this person's own rights,
   * and a member who may add a place is not always one who may curate the knowledge base, so a
   * refusal is an ordinary outcome — it costs the index, not the access.
   */
  async function index(
    address: {
      type:
        | 'sharepoint_site'
        | 'sharepoint_library'
        | 'sharepoint_folder'
        | 'sharepoint_file';
      siteId: string;
      driveId?: string;
      itemId: string;
    },
    name: string,
    webUrl: string | undefined,
  ): Promise<void> {
    try {
      await createKnowledgeSource(workspaceId, {
        type: address.type,
        enabled: true,
        config: {
          site_id: address.siteId,
          ...(address.driveId ? { drive_id: address.driveId } : {}),
          item_id: address.itemId,
          connection_id: connectionId,
          name,
          ...(webUrl ? { url: webUrl } : {}),
        },
      });
      await queryClient.invalidateQueries({
        queryKey: knowledgeSourceKeys.list(workspaceId),
      });
      if (
        address.type === 'sharepoint_site' ||
        address.type === 'sharepoint_library'
      ) {
        notify.info(WHOLE_SITE_OR_LIBRARY_NOTE(name));
      }
    } catch (reason) {
      /**
       * Already on the shelf — a folder this workspace indexed before places existed, now allowed
       * too. That is the state that was asked for, so it is not reported as a failure.
       */
      if (reason instanceof KnowledgeSourceAlreadyAdded) {
        await queryClient.invalidateQueries({
          queryKey: knowledgeSourceKeys.list(workspaceId),
        });
        return;
      }
      /**
       * The place IS on the list — only the indexing did not happen, so this is a warning and not a
       * failure, and it says which of the two it was. The server's own reason travels with it:
       * swallowing it left one sentence for every cause, and a refusal for size reads nothing like
       * a refusal for rights.
       */
      notify.warning(
        `“${name}” is on the list, but it was not added to the knowledge base. ${errorText(reason, 'The knowledge base did not accept it.')}`,
      );
    }
  }

  /** Adds one row as it is, then hands the refreshed list back to whoever mounted this. */
  async function add(
    rowId: string,
    run: () => Promise<SharePointPlace | undefined>,
  ): Promise<void> {
    if (!session) return;
    if (Date.parse(session.expiresAt) <= Date.now()) {
      setError(
        'This setup session expired. Close and reopen this dialog to try again.',
      );
      return;
    }
    setPendingAction({ kind: 'add', rowId });
    setError(null);
    try {
      onAdded(await run());
    } catch (reason) {
      setError(errorText(reason, 'Could not add this.'));
    } finally {
      setPendingAction(null);
    }
  }

  function addSite(site: SharePointSite): Promise<void> {
    return add(site.siteId, async () => {
      const place = await createSharePointPlace({
        workspaceId,
        connectionId,
        level: 'site',
        siteId: site.siteId,
        interactiveAccess: 'read_write',
        automationAccess: 'read_write',
      });
      await index(
        { type: 'sharepoint_site', siteId: site.siteId, itemId: site.siteId },
        site.name,
        site.webUrl,
      );
      return place;
    });
  }

  function addDrive(drive: SharePointDrive): Promise<void> {
    return add(drive.driveId, async () => {
      if (!siteId) throw new Error('No site is open.');
      const place = await createSharePointPlace({
        workspaceId,
        connectionId,
        level: 'library',
        siteId,
        driveId: drive.driveId,
        interactiveAccess: 'read_write',
        automationAccess: 'read_write',
      });
      await index(
        {
          type: 'sharepoint_library',
          siteId,
          driveId: drive.driveId,
          itemId: drive.driveId,
        },
        drive.name,
        drive.webUrl,
      );
      return place;
    });
  }

  function addItem(item: SharePointItem): Promise<void> {
    return add(item.itemId, async () => {
      if (!siteId || !driveId) throw new Error('No library is open.');
      const place = await createSharePointPlace({
        workspaceId,
        connectionId,
        level: item.isFolder ? 'folder' : 'file',
        siteId,
        driveId,
        rootItemId: item.itemId,
        interactiveAccess: 'read_write',
        automationAccess: 'read_write',
      });
      await index(
        {
          type: item.isFolder ? 'sharepoint_folder' : 'sharepoint_file',
          siteId,
          driveId,
          itemId: item.itemId,
        },
        item.name,
        item.webUrl,
      );
      return place;
    });
  }

  /**
   * Why a row cannot be added, in words, under its name — or nothing when it can.
   *
   * The marks are not decoration: identity refuses an overlapping place, so an Add on a row an
   * existing place already covers is a button that can only fail — and it did, with the server's
   * raw error on screen. `Included` says the work is done; a disabled Add says the row is reachable
   * only after the narrower places inside it go.
   *
   * It is on screen and not in a `title` because a title is not an answer: it never appears on a
   * touch screen, and on a pointer only if somebody thinks to hover a control that already looks
   * dead. The OneDrive half shipped exactly that and its whole-drive row was reported as simply
   * unavailable.
   */
  function blockedNote(
    kind: 'site' | 'library' | 'folder' | 'file',
    id: string,
  ): string | undefined {
    const standing = standingOf({ kind, id }, position);
    if (standing.kind !== 'contains') return undefined;
    const count = standing.blocking.length;
    if (!onReplace) {
      return count === 1
        ? 'A narrower place inside this one is on the list — remove it to add the whole thing.'
        : `${count} narrower places inside this one are on the list — remove them to add the whole thing.`;
    }
    return count === 1
      ? 'Covers a place already on the list.'
      : `Covers ${count} places already on the list.`;
  }

  /** The rows already on the list that this one would swallow, empty when it swallows none. */
  function blockingOf(
    kind: 'site' | 'library' | 'folder' | 'file',
    id: string,
  ): readonly BlockingRow[] {
    const standing = standingOf({ kind, id }, position);
    return standing.kind === 'contains' ? standing.blocking : [];
  }

  /**
   * The Add on a row: straight through when nothing is in the way, a confirmation when something is.
   *
   * A row that covers places already on the list used to be a dead button with an instruction under
   * it — go and remove them, one at a time, each behind its own confirmation. The work is the same
   * either way; the difference is who does it.
   *
   * Without `onReplace` there is nobody to do the removing, so the row stays refused and says why.
   */
  function askToAdd(
    kind: 'site' | 'library' | 'folder' | 'file',
    id: string,
    name: string,
    run: () => void,
  ): void {
    const blocking = blockingOf(kind, id);
    if (blocking.length === 0) {
      run();
      return;
    }
    setConfirming({ rowId: id, name, blocking, run });
  }

  /**
   * Takes the blocking places off the list, then adds the one that was asked for.
   *
   * In that order, and it cannot be the other way round: identity refuses a place that overlaps an
   * existing one, so the old ones have to be gone before the new one can exist. A failure after the
   * removals leaves the list shorter and the new place unadded — which is why the confirmation says
   * what comes off before any of it happens. Every other Add waits meanwhile: the list is mid-change.
   */
  async function confirmAdd(): Promise<void> {
    const pending = confirming;
    if (!pending || !onReplace) return;
    setConfirming(null);
    setError(null);
    setPendingAction({ kind: 'add', rowId: pending.rowId });
    try {
      await onReplace(pending.blocking.map((row) => row.id));
    } catch (reason) {
      setError(errorText(reason, 'Could not take the places off the list.'));
      return;
    } finally {
      setPendingAction(null);
    }
    pending.run();
  }

  function rowAction(
    kind: 'site' | 'library' | 'folder' | 'file',
    id: string,
    name: string,
    onAdd: () => void,
  ): JSX.Element {
    const standing = standingOf({ kind, id }, position);
    if (standing.kind === 'added') {
      return (
        <span className="inline-flex h-8 shrink-0 items-center gap-1 px-2 text-xs text-muted-foreground">
          <CheckIcon className="size-3" aria-hidden />
          Added
        </span>
      );
    }
    if (standing.kind === 'covered') {
      return (
        <span
          className="inline-flex h-8 shrink-0 items-center gap-1 px-2 text-xs text-muted-foreground"
          title={`Already reachable through ${standing.by}.`}
        >
          <CheckIcon className="size-3" aria-hidden />
          Included
        </span>
      );
    }
    const adding = pendingAction?.kind === 'add' && pendingAction.rowId === id;
    /** Refused only where nobody can clear what is in the way — see {@link blockedNote}. */
    const blocked = standing.kind === 'contains' && !onReplace;
    return (
      <Button
        size="sm"
        variant="outline"
        aria-label={
          blocked
            ? `Cannot add ${name} — remove the narrower places inside it first`
            : `Add ${name}`
        }
        disabled={busy || blocked}
        onClick={onAdd}
      >
        {adding ? <Loader2Icon className="animate-spin" aria-hidden /> : 'Add'}
      </Button>
    );
  }

  const crumbs: BrowseCrumb[] = [
    { key: 'sites', label: 'Sites', onSelect: backToSites },
  ];
  if (siteId !== null) {
    crumbs.push({
      key: siteId,
      label: siteName ?? 'Site',
      onSelect: backToDrives,
    });
  }
  if (driveId !== null) {
    crumbs.push({
      key: driveId,
      label: driveName ?? 'Library',
      onSelect: () => void backToFolder(-1),
    });
  }
  folderStack.forEach((folder, index) => {
    crumbs.push({
      key: folder.itemId,
      label: folder.name,
      onSelect: () => void backToFolder(index),
    });
  });

  const covering = coverOf(position);

  return (
    <div
      aria-label="Browse this SharePoint"
      aria-busy={busy}
      className="flex min-h-0 flex-1 flex-col gap-2"
    >
      {/*
        Starting and failing wear the same box the tree does, so the column keeps its frame and its
        height while it waits. Without it the panel beside this one stood alone for a second and the
        dialog looked half-built.
      */}
      {!session && (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 rounded-xl border border-border p-2">
          {error ? (
            <>
              <p className="px-3 text-center text-sm text-destructive">
                {error}
              </p>
              <Button variant="outline" onClick={() => void start()}>
                Try again
              </Button>
            </>
          ) : (
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2Icon className="animate-spin" aria-hidden />
              Starting…
            </span>
          )}
        </div>
      )}

      {session && (
        /*
          Where you are lives INSIDE the box, pinned above the rows that scroll under it. Sitting
          outside, it pushed this box a breadcrumb's height below the one beside it, and the two
          columns started on different lines — which is the thing the columns were meant to fix.

          The tree scrolls inside its own box for the older reason: a connection can reach seventy
          sites, and letting that list set the dialog's height pushed the chosen ones and the Done
          button off the screen.
        */
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-border">
          <PlaceFinder
            workspaceId={workspaceId}
            connectionId={connectionId}
            setupToken={session.token}
            disabled={busy}
            onReveal={reveal}
          >
            <div className="flex shrink-0 flex-col gap-1 border-b border-border px-2 py-2">
              <SharePointBreadcrumb crumbs={crumbs} />
              {items.length === 1 && items[0]?.itemId === aloneId && (
                <p className="text-xs text-muted-foreground">
                  {HIDDEN_PATH_NOTE}
                </p>
              )}
              {covering !== null && (
                <p className="text-xs text-muted-foreground">
                  All of {covering} is on the list, so nothing in here needs
                  adding.
                </p>
              )}
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-2">
              {siteId === null ? (
                <SharePointSiteList
                  list={browseList(
                    foundFirst(byName(sites), foundId, (row) => row.siteId),
                    sitesCursor,
                    loadingLevel,
                    loadingMore,
                    () => void loadMore(),
                  )}
                  onPick={(site) => void openSite(site)}
                  renderNote={(site) => blockedNote('site', site.siteId)}
                  renderAction={(site) =>
                    rowAction('site', site.siteId, site.name, () =>
                      askToAdd(
                        'site',
                        site.siteId,
                        site.name,
                        () => void addSite(site),
                      ),
                    )
                  }
                />
              ) : driveId === null ? (
                <SharePointDriveList
                  list={browseList(
                    foundFirst(byName(drives), foundId, (row) => row.driveId),
                    drivesCursor,
                    loadingLevel,
                    loadingMore,
                    () => void loadMore(),
                  )}
                  onPick={(drive) => void openDrive(drive)}
                  renderNote={(drive) => blockedNote('library', drive.driveId)}
                  renderAction={(drive) =>
                    rowAction('library', drive.driveId, drive.name, () =>
                      askToAdd(
                        'library',
                        drive.driveId,
                        drive.name,
                        () => void addDrive(drive),
                      ),
                    )
                  }
                />
              ) : (
                <SharePointItemList
                  list={browseList(
                    foundFirst(
                      byFolderThenName(items),
                      foundId,
                      (row) => row.itemId,
                    ),
                    itemsCursor,
                    loadingLevel,
                    loadingMore,
                    () => void loadMore(),
                  )}
                  onOpenFolder={(item) => void openFolder(item)}
                  renderNote={(item) =>
                    blockedNote(item.isFolder ? 'folder' : 'file', item.itemId)
                  }
                  renderItemAction={(item) =>
                    rowAction(
                      item.isFolder ? 'folder' : 'file',
                      item.itemId,
                      item.name,
                      () =>
                        askToAdd(
                          item.isFolder ? 'folder' : 'file',
                          item.itemId,
                          item.name,
                          () => void addItem(item),
                        ),
                    )
                  }
                />
              )}
            </div>
          </PlaceFinder>
          {error && (
            <p className="shrink-0 border-t border-border px-2 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
        </div>
      )}

      <AppDialog
        open={confirming !== null}
        onOpenChange={(next) => !next && setConfirming(null)}
        title={`Add “${confirming?.name ?? ''}”?`}
        description="This covers places that are already on the list, at read and write, in chat and in automations."
      >
        <div className="flex flex-col gap-4 pt-4">
          {confirming && (
            <div className="flex flex-col gap-2 rounded-lg border border-border p-3 text-sm">
              <p className="font-medium">
                {confirming.blocking.length === 1
                  ? 'This place comes off the list'
                  : `These ${confirming.blocking.length} places come off the list`}
              </p>
              <ul className="flex flex-col gap-1 text-muted-foreground">
                {confirming.blocking.map((row) => (
                  <li key={row.id} className="truncate">
                    {row.name}
                  </li>
                ))}
              </ul>
              {/*
                The part that does not come back. What is being added reaches everything they
                reached, so nothing becomes unreachable — but each carries its own access settings
                and its own place in the knowledge base, and both go with it.
              */}
              <p className="text-xs text-muted-foreground">
                Everything they reached stays reachable. What they lose is their
                own access settings and their place in the knowledge base —
                anything indexed under them is deleted, and indexed again from
                scratch under the new one.
              </p>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button onClick={() => void confirmAdd()}>Replace and add</Button>
          </div>
        </div>
      </AppDialog>
    </div>
  );
}
